import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  FileText,
  Lock,
  Unlock,
  Eye
} from "lucide-react";

import {
  getAllMarksAssignments,
  updateMarksAssignmentStatus
} from "../../api/markschedule/marksApi";
import {
  getAllMarksAssignmentsPortal,
  updateMarksAssignmentStatusPortal
} from "../../api/markschedule/marksPortalApi";
import {
  getAllClasses
} from "../../api/student/studentApi";
import {
  getAllClassesForPortal
} from "../../api/student/studentPortalApi";
import {
  getAllAcademicYears,
  getActiveAcademicYear
} from "../../api/academic/academicYearApi";
import {
  getAllAcademicYearsPortal,
  getActiveAcademicYearPortal
} from "../../api/academic/academicYearPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import AssignmentStatusBadge from "../../components/markschedule/AssignmentStatusBadge";
import {
  formatDate,
  pickList,
  toId
} from "../../utils/markschedule/marksHelpers";
import {
  Button,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const resolveYearName = (res) => {
  if (!res) return "";
  const y = res.academicYear || res.data || res;
  if (typeof y === "string") return y.trim();
  return (y.yearName || y.name || y.academicYear || y.label || "")
    .toString()
    .trim();
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "LOCKED", label: "Locked" },
];

/**
 * Admin view of all marks assignments (including teacher submissions).
 * Filters: active academic year (default), class, status.
 * Open row → entry view (read-only when SUBMITTED/LOCKED).
 * Shape is report-card friendly via assignment + entryStats.
 */
const MarksAssignmentsPage = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearReady, setYearReady] = useState(false);
  const [years, setYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({
    academicYear: "",
    classId: "",
    status: "SUBMITTED",
  });

  const toast = useToast();

  const entryPath = isPortal
    ? "/staff-panel/marks/entry"
    : "/marks/entry";
  const eligiblePath = isPortal
    ? "/staff-panel/marks/eligible"
    : "/marks/eligible";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allRes, activeRes, classRes] = await Promise.all([
          isPortal
            ? getAllAcademicYearsPortal({})
            : getAllAcademicYears({}),
          isPortal
            ? getActiveAcademicYearPortal()
            : getActiveAcademicYear(),
          isPortal ? getAllClassesForPortal() : getAllClasses(),
        ]);
        if (cancelled) return;
        const list = pickList(allRes, ["academicYears", "years", "data"]);
        const names = list
          .map((y) =>
            typeof y === "string"
              ? y
              : y.yearName || y.name || y.academicYear || ""
          )
          .filter(Boolean);
        const active = resolveYearName(activeRes);
        const yearSet = new Set(names);
        if (active) yearSet.add(active);
        setYears([...yearSet]);
        setClasses(pickList(classRes, ["classes", "data"]));
        setFilters((f) => ({
          ...f,
          academicYear: active || names[0] || "",
        }));
      } catch (e) {
        // non-fatal
      } finally {
        if (!cancelled) setYearReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  const load = useCallback(async () => {
    if (!yearReady) return;
    try {
      setLoading(true);
      const q = {
        academicYear: filters.academicYear || undefined,
        classId: filters.classId || undefined,
        status: filters.status || undefined,
      };
      const res = isPortal
        ? await getAllMarksAssignmentsPortal(q)
        : await getAllMarksAssignments(q);
      setAssignments(pickList(res, ["assignments", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load assignments");
    } finally {
      setLoading(false);
    }
  }, [isPortal, filters, yearReady]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLock = async (id) => {
    if (!canManage) return;
    try {
      if (isPortal)
        await updateMarksAssignmentStatusPortal(id, { status: "LOCKED" });
      else await updateMarksAssignmentStatus(id, { status: "LOCKED" });
      toast.success("Assignment locked");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Lock failed");
    }
  };

  const handleReopen = async (id) => {
    if (!canManage) return;
    if (!window.confirm("Reopen to IN_PROGRESS so teacher can edit again?"))
      return;
    try {
      if (isPortal)
        await updateMarksAssignmentStatusPortal(id, {
          status: "IN_PROGRESS",
        });
      else
        await updateMarksAssignmentStatus(id, { status: "IN_PROGRESS" });
      toast.success("Assignment reopened");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Reopen failed");
    }
  };

  const yearOptions = useMemo(
    () => [
      { label: "All years", value: "" },
      ...years.map((y) => ({ label: y, value: y })),
    ],
    [years]
  );

  const classOptions = useMemo(
    () => [
      { label: "All classes", value: "" },
      ...classes.map((c) => ({
        label: c.className || c.name,
        value: toId(c),
      })),
    ],
    [classes]
  );

  const columns = [
    {
      key: "examSubject",
      header: "Exam / Subject",
      render: (a) => (
        <div>
          <div className="font-medium text-neutral-900 dark:text-neutral-100">
            {a.examName || a.examId?.name || "Exam"}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            {a.subjectId?.name || "Subject"} · Max {a.maxMarks} ·{" "}
            {formatDate(a.examDate)} · {a.academicYear}
          </div>
        </div>
      ),
    },
    {
      key: "classSection",
      header: "Class · Section",
      render: (a) => (
        <span className="text-neutral-700 dark:text-neutral-200">
          {a.classId?.className || "—"} · {a.sectionId?.sectionName || "—"}
        </span>
      ),
    },
    {
      key: "teacher",
      header: "Teacher",
      render: (a) => (
        <span className="text-neutral-700 dark:text-neutral-200">
          {a.assignedStaffId?.name || "—"}
          {a.assignedStaffId?.staffCode
            ? ` (${a.assignedStaffId.staffCode})`
            : ""}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (a) => (
        <div>
          <AssignmentStatusBadge status={a.status} />
          {a.submittedAt && (
            <div className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-1">
              {formatDate(a.submittedAt)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "entries",
      header: "Entries",
      render: (a) => {
        const stats = a.entryStats || {};
        return (
          <span className="text-xs text-neutral-600 dark:text-neutral-300">
            {stats.entered ?? 0}/{stats.total ?? 0}
            {stats.absent ? ` · ${stats.absent} absent` : ""}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (a) => {
        const id = toId(a);
        return (
          <div className="inline-flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => navigate(`${entryPath}/${id}`)}
              leftIcon={<Eye className="h-3.5 w-3.5" />}
              className="text-primary-900 dark:text-primary-300 hover:underline"
            >
              View Marks
            </Button>
            {canManage && a.status === "SUBMITTED" && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => handleLock(id)}
                leftIcon={<Lock className="h-3.5 w-3.5" />}
                className="text-emerald-700 hover:bg-emerald-50"
              >
                Lock
              </Button>
            )}
            {canManage &&
              (a.status === "SUBMITTED" || a.status === "LOCKED") && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => handleReopen(id)}
                  leftIcon={<Unlock className="h-3.5 w-3.5" />}
                  className="text-amber-700 hover:bg-amber-50"
                >
                  Reopen
                </Button>
              )}
          </div>
        );
      },
    },
  ];

  const tableRows = useMemo(
    () => assignments.map((a) => ({ ...a, id: toId(a) })),
    [assignments]
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Submitted Marks
          </h1>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
            View teacher submissions by year, class and status. Use for review before report cards.
          </p>
        </div>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          onClick={() => navigate(eligiblePath)}
        >
          Assign Marks
        </Button>
      </div>

      {/* Filter Card */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Select
              label="Academic Year"
              options={yearOptions}
              value={filters.academicYear}
              onChange={(val) =>
                setFilters((f) => ({ ...f, academicYear: val }))
              }
            />
            <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-1">
              Defaults to active year
            </p>
          </div>
          <Select
            label="Class"
            options={classOptions}
            value={filters.classId}
            onChange={(val) => setFilters((f) => ({ ...f, classId: val }))}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={filters.status}
            onChange={(val) => setFilters((f) => ({ ...f, status: val }))}
          />
        </CardContent>
      </Card>

      {/* Alerts */}
      
      

      {/* Table Card */}
      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading || !yearReady}
            emptyState={
              <EmptyState
                icon={FileText}
                title="No assignments found"
                description="No assignments match the selected filters. Default status is Submitted — switch status to All to see pending work."
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default MarksAssignmentsPage;